// --- NEW FILE: packages/components/src/quotes/QuoteHeader.tsx ---

import React from 'react';
import { Card, Badge } from '@dopomogai/ui';
import type { Quote } from '@dopomogai/types';
import { Info } from 'lucide-react';

type QuoteStatus = 'pending' | 'paid' | 'expired';

type Props = {
  quoteId: string;
  status: QuoteStatus;
  validUntil: string;
};

// Maps status to badge color and text
const statusMap: Record<QuoteStatus, { text: string; color: 'blue' | 'green' | 'yellow' }> = {
  pending: { text: 'Pending Payment', color: 'blue' },
  paid: { text: 'Paid', color: 'green' },
  expired: { text: 'Expired', color: 'yellow' },
};

export function QuoteHeader({ quoteId, status, validUntil }: Props) {
  const { text, color } = statusMap[status];

  return (
    <Card noHoverEffect className="mb-6 print:hidden">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Quote #{quoteId.split('-')[0].toUpperCase()}</h2>
          <p className="text-sm text-slate-600">
            Valid until: {new Date(validUntil).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          {/* Info notes: refund policy + magic link */}
          <div className="mt-2 space-y-1 text-xs text-slate-600">
            <div className="flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 text-slate-500" />
              <span>
                Risk‑free start: 100% refundable after the kick‑off call if you decide not to proceed, or anytime up to 14 days before your first onboarding session.
              </span>
            </div>
            <div className="flex items-start gap-1.5">
              <Info className="w-3.5 h-3.5 mt-0.5 text-slate-500" />
              <span>We've emailed your quote and a magic link so you can return to it anytime.</span>
            </div>
          </div>
        </div>
        <Badge text={text} accentColor={color} size="md" />
      </div>
    </Card>
  );
}